import * as React from 'react';
import ReactDOM from 'react-dom';
import { createPopper } from '@popperjs/core';
import { Transition } from 'react-transition-group';
import { tabbable } from 'tabbable';
import BodyScroll from '@telia-front/body-scroll';
import CloseButton from '@telia-front/close-button';
import PageAccess from '@telia-front/page-access';
import { media } from '@telia-front/theme-provider';
import VisibilityProvider from '@telia-front/visibility-provider';
import { HeaderInnerContext } from '../header/header';
const CONTAINER_MAX_WIDTH = 1224;
const HeaderDropdown = (props) => {
    const innerContext = React.useContext(HeaderInnerContext);
    return React.createElement(HeaderDropdownInner, Object.assign({}, props, { innerContext: innerContext }));
};
export default HeaderDropdown;
class HeaderDropdownInner extends React.PureComponent {
    constructor(props) {
        super(props);
        this.content = null;
        this.element = null;
        this.popper = null;
        this.getTransforms = ({ state }) => {
            var _a, _b;
            const reference = state.elements.reference.getBoundingClientRect();
            const popper = state.elements.popper.getBoundingClientRect();
            const documentWidth = document.documentElement ? document.documentElement.clientWidth : 0;
            const popperWidth = popper.width;
            const halfReferenceWidth = reference.width / 2;
            const referenceMiddle = reference.left + halfReferenceWidth;
            // Align popper center of screen
            const idealPopperLeft = referenceMiddle - popperWidth / 2;
            const containerWidth = Math.min((_b = (_a = this.props.innerContext.containerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : CONTAINER_MAX_WIDTH, documentWidth) - 64;
            // If popper is out of container bounds, bring it back in bounds
            const minLeft = (documentWidth - containerWidth) / 2;
            const maxLeft = minLeft + containerWidth - popperWidth;
            const popperLeft = Math.round(Math.max(minLeft, Math.min(idealPopperLeft, maxLeft)));
            const popperTop = Math.round(reference.bottom) + (typeof this.props.offsetY === 'undefined' ? 5 : this.props.offsetY);
            if (this.isSmallerThanHeaderBreak() || this.props.fullWidth) {
                return {
                    popperTransform: `translate(0, ${popperTop}px)`,
                };
            }
            return {
                popperTransform: `translate(${popperLeft}px, ${popperTop}px)`,
            };
        };
        this.close = (event) => {
            var _a;
            event === null || event === void 0 ? void 0 : event.preventDefault();
            (_a = this.props.reference) === null || _a === void 0 ? void 0 : _a.focus();
            if (this.props.onClose) {
                this.props.onClose();
            }
        };
        this.onKeyDown = (event) => {
            switch (event.key) {
                // Invoke onClose when user hits ESC key.
                case 'Escape':
                case 'Esc':
                    this.close(event);
                    break;
                case 'Tab':
                    if (this.element && this.props.asModal) {
                        const tabbableModalElements = tabbable(this.element);
                        if (tabbableModalElements.length) {
                            const firstTabbableElement = tabbableModalElements[0];
                            const lastTabbableElement = tabbableModalElements[tabbableModalElements.length - 1];
                            if (event.shiftKey && document.activeElement === document.getElementById('header-dropdown-button')) {
                                lastTabbableElement.focus();
                            }
                            else if (event.shiftKey && document.activeElement === document.getElementById('header-dropdown-hidden-button')) {
                                lastTabbableElement.focus();
                            }
                            else if (document.activeElement === document.getElementById('header-dropdown-hidden-button')) {
                                firstTabbableElement.focus();
                            }
                            else if (event.shiftKey) {
                                if (document.activeElement === firstTabbableElement) {
                                    event.preventDefault();
                                    lastTabbableElement.focus();
                                }
                            }
                            else {
                                if (document.activeElement === lastTabbableElement) {
                                    event.preventDefault();
                                    firstTabbableElement.focus();
                                }
                            }
                        }
                        else {
                            event.preventDefault();
                        }
                    }
            }
            if (this.props.onKeyDown) {
                this.props.onKeyDown(event);
            }
        };
        this.handleEnter = () => {
            this.initPopper();
            this.props.asModal && this.hijackScrollAndPageAccess();
        };
        this.handleExited = () => {
            this.destroyPopper();
            this.props.asModal && this.restoreScrollAndPageAccess();
            if (this.props.onTransitionExited) {
                this.props.onTransitionExited();
            }
        };
        this.setElementRef = (element) => {
            this.element = element;
            if (this.props.elementRef) {
                this.props.elementRef(element);
            }
        };
        this.setContentRef = (element) => {
            this.content = element;
            if (this.props.contentRef) {
                this.props.contentRef(element);
            }
        };
        this.renderDropdown = (state) => {
            if (!this.state.isMounted) {
                return null;
            }
            return (React.createElement("div", { className: this.BEM(state), onKeyDown: this.onKeyDown, style: this.state.popperStyle, tabIndex: -1, ref: this.setElementRef, onMouseLeave: this.props.onMouseLeave, id: this.props.id, role: this.props.asModal ? 'dialog' : undefined, "aria-modal": this.props.asModal, "aria-labelledby": this.props.asModal ? this.props['aria-labelledby'] : undefined },
                React.createElement("div", { className: "header__dropdown-inner" },
                    React.createElement("div", { className: "header__dropdown-content-wrapper", ref: this.setContentRef },
                        this.props.onClose && this.getCloseButton(),
                        this.props.header && React.createElement("div", { className: "header__dropdown-header" }, this.props.header),
                        React.createElement("div", { className: "header__dropdown-content" }, this.props.children),
                        this.props.footer && React.createElement("div", { className: "header__dropdown-footer" }, this.props.footer),
                        this.props.onClose && this.getHiddenCloseButton()))));
        };
        this.onOutsideClick = this.onOutsideClick.bind(this);
        this.state = {
            isMounted: false,
            popperStyle: {},
            disablePageInteraction: false,
        };
    }
    componentDidMount() {
        this.setState({
            isMounted: true,
        }, () => {
            if (this.isOpen) {
                this.initPopper();
                this.hijackScrollAndPageAccess();
            }
        });
        document.addEventListener('mousedown', this.onOutsideClick);
    }
    get isOpen() {
        return typeof this.props.isShown !== 'undefined' ? this.props.isShown : false;
    }
    componentWillUnmount() {
        this.destroyPopper();
        if (this.isOpen) {
            this.restoreScrollAndPageAccess();
        }
        document.removeEventListener('mousedown', this.onOutsideClick);
    }
    componentDidUpdate(prevProps) {
        // Reinitiate popper to get correct position styles after trigger reference element change
        if (this.props.reference !== prevProps.reference || this.props.container !== prevProps.container || this.props.size !== prevProps.size) {
            this.destroyPopper();
            this.initPopper();
        }
    }
    hijackScrollAndPageAccess() {
        if (!this.state.disablePageInteraction) {
            this.setState({
                disablePageInteraction: true,
            }, () => {
                if (this.element) {
                    const firstTabbableElement = tabbable(this.element)[0];
                    if (firstTabbableElement) {
                        firstTabbableElement.focus();
                        return;
                    }
                    this.element.focus();
                }
            });
        }
    }
    restoreScrollAndPageAccess() {
        if (this.state.disablePageInteraction) {
            this.setState({
                disablePageInteraction: false,
            }, () => {
                var _a;
                if (this.props.reference) {
                    (_a = this.props.reference) === null || _a === void 0 ? void 0 : _a.focus();
                }
            });
        }
    }
    onOutsideClick(event) {
        var _a, _b, _c;
        if (this.isOpen && ((_a = this.content) === null || _a === void 0 ? void 0 : _a.offsetParent)) {
            const clickedOnTrigger = event.target instanceof Element && ((_b = this.props.reference) === null || _b === void 0 ? void 0 : _b.contains(event.target));
            const clickedOnContent = event.target instanceof Node && ((_c = this.content) === null || _c === void 0 ? void 0 : _c.contains(event.target));
            if (clickedOnTrigger || clickedOnContent) {
                return;
            }
            this.close();
        }
    }
    initPopper() {
        if (this.element && this.props.reference && !this.popper && this.props.container) {
            this.popper = createPopper(this.props.reference, this.element, {
                modifiers: [
                    { name: 'applyStyles', enabled: false },
                    {
                        name: 'updateState',
                        enabled: true,
                        phase: 'write',
                        requires: ['computeStyles'],
                        fn: ({ state }) => this.updatePopperState(state),
                    },
                    {
                        name: 'accountForSize',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        effect: ({ state }) => {
                            // Necessary so that dropdown content would scroll correctly
                            state.elements.popper.style.height = `${this.getPopperHeight(state)}px`;
                        },
                        fn: ({ state }) => {
                            state.elements.popper.style.height = `${this.getPopperHeight(state)}px`;
                            if (state.elements.popper.classList.contains('header__nav-megamenu-dropdown')) {
                                state.elements.popper.style.background = `linear-gradient(to bottom, var(--color-neutral-bg-level-1) ${state.elements.popper.children[0].children[0].clientHeight}px, transparent 20px)`;
                            }
                            return state;
                        },
                    },
                    {
                        name: 'accountForCentering',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn: data => {
                            const { popperTransform } = this.getTransforms(data);
                            data.state.styles.popper.transform = popperTransform;
                            if (this.isSmallerThanHeaderBreak()) {
                                data.state.styles.popper.right = '0';
                                data.state.styles.popper.bottom = '0';
                            }
                            return data.state;
                        },
                    },
                    {
                        name: 'flip',
                        enabled: false,
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: this.isSmallerThanHeaderBreak() ? 0 : 30,
                            boundariesElement: this.props.container,
                        },
                    },
                ],
                placement: 'bottom',
                strategy: 'fixed',
            });
        }
    }
    updatePopperState(state) {
        const popperStyle = Object.assign({}, state.styles.popper);
        this.setState({
            popperStyle,
        });
    }
    getPopperHeight(state) {
        const reference = state.elements.reference.getBoundingClientRect();
        const popperTop = Math.round(reference.bottom) + (typeof this.props.offsetY === 'undefined' ? 5 : this.props.offsetY);
        return window.innerHeight - popperTop;
    }
    isSmallerThanHeaderBreak() {
        return window.matchMedia(`(max-width: ${media.width.md.max})`).matches;
    }
    destroyPopper() {
        if (this.popper) {
            this.popper.destroy();
            this.popper = null;
        }
    }
    get padding() {
        const { padded, padding } = this.props;
        if (padding) {
            return padding;
        }
        if (padded) {
            return 'medium';
        }
        return 'none';
    }
    BEM(state) {
        const classArray = ['header__dropdown', `header__dropdown--padding-${this.padding}`, 'is-' + state];
        if (this.props.size) {
            classArray.push('header__dropdown--' + this.props.size);
        }
        if (this.props.className) {
            classArray.push(this.props.className);
        }
        return classArray.join(' ');
    }
    getCloseButton() {
        const { closeButtonLabel } = this.props;
        return React.createElement(CloseButton, { className: "header__dropdown-close", onClick: this.close, attributes: { id: 'header-dropdown-button' }, label: closeButtonLabel });
    }
    getHiddenCloseButton() {
        const { closeButtonLabel } = this.props;
        return (React.createElement(CloseButton, { className: "header__dropdown-close", onClick: this.close, label: closeButtonLabel, attributes: { id: 'header-dropdown-hidden-button' } }));
    }
    renderWithTransition() {
        return (React.createElement(Transition, { in: this.isOpen, timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true, onEntering: this.handleEnter, onExited: this.handleExited, onEntered: this.props.onTransitionEntered }, this.renderDropdown));
    }
    renderHeaderDropdown() {
        return (React.createElement(VisibilityProvider, { isVisible: this.isOpen },
            React.createElement(PageAccess, { isInert: this.state.disablePageInteraction },
                React.createElement(BodyScroll, { isDisabled: this.state.disablePageInteraction }, this.renderWithTransition()))));
    }
    render() {
        var _a;
        if (this.props.asModal) {
            return this.state.isMounted ? ReactDOM.createPortal(this.renderHeaderDropdown(), (_a = this.props.container) !== null && _a !== void 0 ? _a : document.body) : this.renderHeaderDropdown();
        }
        return this.renderWithTransition();
    }
}
HeaderDropdownInner.defaultProps = {
    size: 'large',
};
